import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Download, Loader2, Lock } from 'lucide-react';
import { hcApi, hcError } from './api';
import { HcLayout } from './HcLayout';
import { downloadCertificate } from './HcCertificateBits';

const CertificateCard = ({ cert, busy, setBusy }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-4" data-testid={`hc-certificate-${cert.id}`}>
    <div className="flex items-start gap-4">
      <div className="w-14 h-14 rounded-2xl bg-[#F6EAF5] text-[#922A8C] flex items-center justify-center shrink-0"><Award className="w-7 h-7" /></div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest font-bold text-[#922A8C]">Certificate of Achievement</div>
        <h3 className="font-bold text-lg text-[#363636] mt-1" data-testid={`hc-certificate-title-${cert.id}`}>{cert.course_title}</h3>
      </div>
    </div>
    <dl className="grid grid-cols-2 gap-3 text-sm">
      <div><dt className="text-gray-500">Certificate Number</dt><dd className="font-semibold text-[#363636]" data-testid={`hc-certificate-number-${cert.id}`}>{cert.number}</dd></div>
      <div><dt className="text-gray-500">Completed</dt><dd className="font-semibold text-[#363636]" data-testid={`hc-certificate-date-${cert.id}`}>{cert.completion_date}</dd></div>
    </dl>
    <button onClick={() => downloadCertificate(cert, setBusy)} disabled={busy === cert.id} data-testid={`hc-certificate-download-${cert.id}`}
      className="mt-auto h-12 rounded-xl bg-[#922A8C] text-white font-semibold hover:bg-[#7a2275] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
      {busy === cert.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download PDF
    </button>
  </div>
);

const PendingRow = ({ item, passing, onOpen }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-5" data-testid={`hc-certificate-pending-${item.course_id}`}>
    <Lock className="w-5 h-5 text-gray-400 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-[#363636]">{item.course_title}</div>
      <div className="text-sm text-gray-500">
        {item.requirement === 'quiz_score'
          ? `Score ${passing}% or higher on every quiz in this course to earn your certificate.`
          : `Finish every lesson to earn your certificate. ${item.progress}% complete.`}
      </div>
    </div>
    <button onClick={onOpen} className="h-10 px-5 rounded-xl border border-[#922A8C] text-[#922A8C] font-semibold hover:bg-[#F6EAF5] transition-colors"
      data-testid={`hc-certificate-pending-open-${item.course_id}`}>Go to course</button>
  </div>
);

export default function HcCertificatesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    hcApi.get('/certificates').then(({ data: d }) => setData(d)).catch((e) => setError(hcError(e)));
  }, []);

  return (
    <HcLayout>
      <div className="max-w-6xl hc-fade-up" data-testid="hc-certificates-page">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#363636]">Certificates</h1>
        <p className="text-gray-500 mt-2">Download a certificate for every course you complete.</p>
        {!data && !error && <Loader2 className="w-8 h-8 animate-spin text-[#922A8C] mt-6" />}
        {error && <p className="text-red-600 mt-6" data-testid="hc-certificates-error">{error}</p>}
        {data && (
          <>
            {data.certificates.length === 0 ? (
              <div className="mt-6 bg-white rounded-2xl border border-gray-100 p-8 text-gray-500" data-testid="hc-certificates-empty">
                You haven't earned a certificate yet. Finish every lesson in a course and score {data.passing_score}% or higher on its quizzes to earn one.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 mt-6" data-testid="hc-certificates-list">
                {data.certificates.map((c) => <CertificateCard key={c.id} cert={c} busy={busy} setBusy={setBusy} />)}
              </div>
            )}
            {data.pending.length > 0 && (
              <>
                <h2 className="text-base md:text-lg font-bold text-[#363636] mt-10 mb-3">Still to earn</h2>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100" data-testid="hc-certificates-pending-list">
                  {data.pending.map((p) => (
                    <PendingRow key={p.course_id} item={p} passing={data.passing_score} onOpen={() => navigate(`/helpconnect/courses/${p.course_id}`)} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </HcLayout>
  );
}
